/**
 * One-shot host helper that takes the already-encoded legacy
 * PoseBinary bytes for a recording, lifts them into a v3.2
 * CanonicalRecording (optionally running the Phase 12 lifter and the
 * Phase 13 depth pass), and returns the gzipped on-disk bytes ready
 * for `RecordingBlob.canonicalBin`.
 *
 * This wraps:
 *   migrateLegacyPoseBinaryToCanonical → buildCanonicalSavePayload
 *   → encodeCanonicalRecording
 *
 * docs/migration-plan.md Phase 12, 13.
 */

import type { PoseLifterAdapter } from '../core/PoseLifter';
import type { SubjectScaleOptions } from '../core/SubjectScaleEstimator';
import {
  buildCanonicalSavePayload,
  type DepthEstimatorLike,
} from './CanonicalSavePipeline';
import { encodeCanonicalRecording } from './CanonicalPersistenceFormat';
import { migrateLegacyPoseBinaryToCanonical } from './MigrateV2';

export interface BuildCanonicalBinOpts {
  poseBin: Uint8Array;
  /** Stable hash, never the raw device id. */
  deviceIdHash: string;
  /** Optional cross-session correlator. */
  sessionId?: string | null;
  /** Phase 12. Pass null/undefined to skip the lifter pass. */
  lifter?: PoseLifterAdapter | null;
  lifterWindowStride?: number;
  /** Phase 13. When provided, depth runs on the representative image
   *  + frame supplied alongside. */
  depth?: DepthEstimatorLike | null;
  representativeImage?: CanvasImageSource | null;
  scaleOptions?: SubjectScaleOptions;
  /** Index of the representative frame in the migrated frame array.
   *  Defaults to the centre frame. */
  representativeFrameIndex?: number;
  /** Optional 3x3 row-major gravity rotation. Applied to MediaPipe-
   *  derived pos3DLocal so downstream ROM consumers see metres
   *  aligned with gravity rather than camera pitch. Pass null to
   *  skip; pass GravityAnchor.buildGravityRotation() to enable. */
  gravityRotation?: readonly number[] | null;
}

export interface BuildCanonicalBinResult {
  bytes: Uint8Array;
  diagnostics: {
    formatVersion: string;
    frameCount: number;
    lifterRan: boolean;
    depthRan: boolean;
    gravityApplied: boolean;
    errors: string[];
  };
}

export async function buildCanonicalBinFromPoseBin(
  opts: BuildCanonicalBinOpts,
): Promise<BuildCanonicalBinResult> {
  const migrated = await migrateLegacyPoseBinaryToCanonical(opts.poseBin, {
    streamId: `legacy-${opts.deviceIdHash}-${Date.now().toString(36)}`,
    deviceIdHash: opts.deviceIdHash,
    sessionId: opts.sessionId ?? null,
    gravityRotation: opts.gravityRotation,
  });
  const frames = migrated.frames;
  const repIndex = clampFrameIndex(opts.representativeFrameIndex, frames.length);
  const representativeFrame = frames[repIndex] ?? null;

  const result = await buildCanonicalSavePayload({
    header: migrated.header,
    frames,
    subject: migrated.subject,
    sessionId: migrated.sessionId,
    lifter: opts.lifter ?? null,
    lifterWindowStride: opts.lifterWindowStride,
    depth: opts.depth ?? null,
    representativeImage: opts.representativeImage ?? null,
    representativeFrame,
    scaleOptions: opts.scaleOptions,
  });

  const bytes = encodeCanonicalRecording(result.recording);
  return {
    bytes,
    diagnostics: {
      formatVersion: result.recording.formatVersion,
      frameCount: result.recording.frames.length,
      lifterRan: result.diagnostics.lifterRan,
      depthRan: result.diagnostics.depthRan,
      gravityApplied: migrated.gravityApplied,
      errors: result.diagnostics.errors,
    },
  };
}

function clampFrameIndex(idx: number | undefined, total: number): number {
  if (total <= 0) return 0;
  if (typeof idx !== 'number' || !Number.isFinite(idx)) return Math.floor(total / 2);
  if (idx < 0) return 0;
  if (idx >= total) return total - 1;
  return Math.floor(idx);
}
